import { useEffect, useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  formType?: 'personal' | 'work' | 'education';
  isLoading?: boolean;
  action?: 'refreshChat' | 'uploadResume';
}

interface ResumeData {
  selectedTemplateId?: string;
  templateId?: string;
  personalInfo?: any;
  summary?: string;
  skills?: string[];
  technicalSkills?: string[];
  softSkills?: string[];
  languages?: string[];
  certifications?: string[];
  workExperience?: any[];
  education?: any[];
  references?: any[];
}

interface UseChatPersistenceProps {
  messages: Message[];
  resumeData: Partial<ResumeData>;
  currentStep: string;
  isConversationComplete: boolean;
  showTemplateSelection: boolean;
  showJobDescriptionInput: boolean;
  showATSPreview: boolean;
  showFormType: "personal" | "work" | "education" | null;
  waitingForChoice: boolean;
  setMessages: (messages: Message[]) => void;
  setResumeData: (data: Partial<ResumeData>) => void;
  setCurrentStep: (step: string) => void;
  setIsConversationComplete: (complete: boolean) => void;
  setShowTemplateSelection: (show: boolean) => void;
  setShowJobDescriptionInput: (show: boolean) => void;
  setShowATSPreview: (show: boolean) => void;
  setShowFormType: (type: "personal" | "work" | "education" | null) => void;
  setWaitingForChoice: (waiting: boolean) => void;
}

export function useChatPersistence({
  messages,
  resumeData,
  currentStep,
  isConversationComplete,
  showTemplateSelection,
  showJobDescriptionInput,
  showATSPreview,
  showFormType,
  waitingForChoice,
  setMessages,
  setResumeData,
  setCurrentStep,
  setIsConversationComplete,
  setShowTemplateSelection,
  setShowJobDescriptionInput,
  setShowATSPreview,
  setShowFormType,
  setWaitingForChoice,
}: UseChatPersistenceProps) {
  const { user } = useAuth();
  const [hasLoadedSession, setHasLoadedSession] = useState(false);

  const getLocalUiState = useCallback(() => {
    const localCurrentStep = localStorage.getItem('resumeBuilder.currentStep');
    const localShowJobDescriptionInput = localStorage.getItem('resumeBuilder.showJobDescriptionInput') === 'true';

    return {
      currentStep: localCurrentStep || '',
      showJobDescriptionInput: localShowJobDescriptionInput,
    };
  }, []);

  useEffect(() => {
    if (user) {
      setHasLoadedSession(false);
      loadChatSession();
    } else {
      setHasLoadedSession(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && hasLoadedSession && messages.length > 0) {
      const timeoutId = setTimeout(() => {
        saveChatSession();
      }, 800);
      return () => clearTimeout(timeoutId);
    }
  }, [
    messages,
    resumeData,
    currentStep,
    isConversationComplete,
    showTemplateSelection,
    showJobDescriptionInput,
    showATSPreview,
    showFormType,
    waitingForChoice,
    user,
    hasLoadedSession,
  ]);

  const loadChatSession = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const messagesArray = Array.isArray(data.messages) ? data.messages : [];
        const parsedMessages = messagesArray.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
          // Drop transient loading flag on restore
          isLoading: false,
        }));

        const ext = data as any;
        const localUiState = getLocalUiState();
        const restoredStep = data.current_step || localUiState.currentStep || "greeting";
        const shouldShowJobDescriptionInput =
          !!ext.show_job_description_input ||
          restoredStep === 'job-tailoring' ||
          (localUiState.currentStep === 'job-tailoring' && localUiState.showJobDescriptionInput);

        setMessages(parsedMessages);
        setResumeData((data.resume_data as Partial<ResumeData>) || {});
        setCurrentStep(shouldShowJobDescriptionInput ? 'job-tailoring' : restoredStep);
        setIsConversationComplete(data.is_conversation_complete || false);

        setShowTemplateSelection(shouldShowJobDescriptionInput ? false : !!ext.show_template_selection);
        setShowJobDescriptionInput(shouldShowJobDescriptionInput);
        setShowATSPreview(shouldShowJobDescriptionInput ? false : !!ext.show_ats_preview);
        const sft = ext.show_form_type;
        setShowFormType(shouldShowJobDescriptionInput ? null : sft === 'personal' || sft === 'work' || sft === 'education' ? sft : null);
        setWaitingForChoice(shouldShowJobDescriptionInput ? false : !!ext.waiting_for_choice);
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
    } finally {
      setHasLoadedSession(true);
    }
  };

  const saveChatSession = async () => {
    if (!user) return;

    try {
      // Persist all UI flags so we restore the exact UI state on reload.
      const shouldPersistJobDescriptionInput = showJobDescriptionInput || currentStep === 'job-tailoring';
      const payload: any = {
        user_id: user.id,
        messages: messages as any,
        resume_data: resumeData as any,
        current_step: shouldPersistJobDescriptionInput ? 'job-tailoring' : currentStep,
        is_conversation_complete: isConversationComplete,
        show_form_type: shouldPersistJobDescriptionInput ? null : showFormType,
        waiting_for_choice: shouldPersistJobDescriptionInput ? false : waitingForChoice,
        show_template_selection: shouldPersistJobDescriptionInput ? false : showTemplateSelection,
        show_job_description_input: shouldPersistJobDescriptionInput,
        show_ats_preview: shouldPersistJobDescriptionInput ? false : showATSPreview,
      };

      const { error } = await supabase
        .from('chat_sessions')
        .upsert(payload, {
          onConflict: 'user_id',
          ignoreDuplicates: false,
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving chat session:', error);
    }
  };

  const clearChatSession = useCallback(async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('user_id', user.id);
      if (error) throw error;
    } catch (error) {
      console.error('Error clearing chat session:', error);
    }
  }, [user]);

  return { clearChatSession };
}
